import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  ApprovalStatus,
  CaptainSummary,
  CaptainTeam,
  CreateCaptainPayload,
  CreatedCaptain,
  DraftBusinessForm,
  PaymentLinkResult,
  PendingBusiness,
  SettleCaptainPayload,
  SettledCaptain,
  RecordSettlementPayload,
  RequestSettlementInput,
  Settlement,
  SettlementRequest,
  SettlementRequestStatus,
  TeamCaptain,
} from "../../interfaces/OnboardingObject";
import { postRequest, putRequest } from "../http/httpRequest";
import {
  ApiEnvelopeError,
  coerceSingle,
  unwrapEnvelope,
} from "../helpers/ApiEnvelopeHelper";
import {
  buildCreatePendingBusinessPayload,
  buildRequestSettlementPayload,
  buildRetryPaymentLinkPayload,
} from "../converters/uiToBackend/OnboardingConvertor";
import {
  convertCaptainSummary,
  convertCreatedCaptain,
  convertCaptainTeam,
  convertPaymentLinkResult,
  convertPendingBusiness,
  convertPendingBusinessList,
  convertSettledCaptain,
  convertSettlement,
  convertSettlementList,
  convertSettlementRequest,
  convertSettlementRequestList,
} from "../converters/backendToUi/onboardingConvertor";

// All onboarding endpoints are POST + bearer token + new-style envelope.
const BEARER = true;

/** Backend errorCode → user-facing copy. Codes not listed fall back to the
 *  server's errorMessage (already human-readable per the API contract). */
const FRIENDLY_MESSAGES: Record<string, string> = {
  NOT_A_LEAD: "Only team leads can do this.",
  CAPTAIN_ALREADY_EXISTS: "This user is already a captain.",
  USER_NOT_FOUND: "No NineFinds account found for this user.",
  // The UI can't show the ceiling up front (no pre-submit endpoint exposes the
  // live onboarding fee), so this message is the captain's ONLY feedback that
  // they've gone over it — keep it explicit about the cause.
  INVALID_CHARGED_AMOUNT:
    "That amount isn't allowed. It can't be negative or more than the standard onboarding fee.",
  INVALID_BUSINESS_PAYLOAD: "Some required business details are missing.",
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  CAPTAIN_NOT_FOUND: "This captain no longer exists. Refresh and try again.",
  // retry-payment-link
  PAYMENT_ALREADY_SETTLED:
    "This payment is already settled — no link to retry.",
  PENDING_BUSINESS_NOT_FOUND: "This submission couldn't be found. Refresh and try again.",
  // settlement requests
  NOTHING_TO_SETTLE: "There's nothing to settle right now.",
  REQUEST_ALREADY_PENDING: "You already have a settlement request waiting.",
  SETTLEMENT_REQUEST_NOT_FOUND: "This request couldn't be found. Refresh and try again.",
  SETTLEMENT_REQUEST_NOT_PENDING: "This request has already been closed. Refresh to see the latest.",
  INVALID_PAYOUT_DETAILS: "Please check your payment details.",
  VALIDATION_ERROR: "Some details aren't valid. Please check and try again.",
};

/** Converts any error thrown by this service into a display string. */
export function toFriendlyOnboardingError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/** API 3 — captain's own dashboard. Throws ApiEnvelopeError(CAPTAIN_NOT_FOUND)
 *  for non-captains; the slice treats that code as a state, not an error. */
async function getCaptainSummary(): Promise<CaptainSummary> {
  const response = await postRequest(ServiceEndpoints.CAPTAIN_SUMMARY, {}, BEARER);
  // coerceSingle: backend array-wraps single objects (legacy builders).
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertCaptainSummary(contents);
}

/** API 2 — captain's own submissions. Omit `status` to fetch all (the UI
 *  filters client-side via status chips). */
async function getPendingBusinesses(
  status?: ApprovalStatus
): Promise<PendingBusiness[]> {
  const response = await postRequest(
    ServiceEndpoints.ONBOARDING_GET_PENDING_BY_CAPTAIN,
    status ? { approvalStatus: status } : {},
    BEARER
  );
  // Converter is shape-tolerant (wrapper object, array-wrapped wrapper, or raw rows).
  const contents = await unwrapEnvelope<unknown>(response);
  return convertPendingBusinessList(contents);
}

/** API 1 — submit a draft business. Idempotent server-side: a double-tap
 *  returns the existing PENDING row as SUCCESS, so callers can treat the
 *  response uniformly. */
async function createPendingBusiness(
  form: DraftBusinessForm
): Promise<PendingBusiness> {
  const payload = buildCreatePendingBusinessPayload(form);
  const response = await postRequest(
    ServiceEndpoints.ONBOARDING_CREATE_PENDING,
    payload,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertPendingBusiness(contents);
}

/** Retry payment-link generation for an existing UNPAID row, keyed by
 *  pendingId only — see buildRetryPaymentLinkPayload for why this no longer
 *  re-calls create-pending-business. Returns just the payment fields; the
 *  thunk merges them onto the full row it already has in state. */
async function retryPaymentLink(pendingId: string): Promise<PaymentLinkResult> {
  const response = await postRequest(
    ServiceEndpoints.ONBOARDING_RETRY_PAYMENT_LINK,
    buildRetryPaymentLinkPayload(pendingId),
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertPaymentLinkResult(contents);
}

/** API 4 — lead registers a new captain under themselves. */
async function createCaptain(
  payload: CreateCaptainPayload
): Promise<CreatedCaptain> {
  const response = await postRequest(
    ServiceEndpoints.CAPTAIN_CREATE,
    payload,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertCreatedCaptain(contents);
}

/** API 5 — lead's team (excludes the lead themselves). */
async function getCaptainsByLead(): Promise<CaptainTeam> {
  const response = await postRequest(
    ServiceEndpoints.CAPTAINS_BY_LEAD,
    {},
    BEARER
  );
  // Converter is shape-tolerant (wrapper object, array-wrapped wrapper, or raw rows).
  const contents = await unwrapEnvelope<unknown>(response);
  return convertCaptainTeam(contents);
}

/** API — update an existing captain's role and/or commission rate. */
async function updateCaptain(
  captainId: string,
  payload: import("../../interfaces/OnboardingObject").UpdateCaptainPayload
): Promise<TeamCaptain> {
  const response = await putRequest(
    `${ServiceEndpoints.UPDATE_CAPTAIN}/${captainId}`,
    payload,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  // In the absence of a specific convertTeamCaptain, we can return it raw or use convertCreatedCaptain 
  // if it matches the shape (which it does, based on the payload)
  return convertCreatedCaptain(contents);
}

/** Records a payout for one captain/lead. Adds `amount` to their totalSettled
 *  and returns the updated captain. No idempotency guard server-side — callers
 *  MUST block double-submit while in flight. */
async function settleCaptain(
  payload: SettleCaptainPayload
): Promise<SettledCaptain> {
  const response = await postRequest(
    ServiceEndpoints.CAPTAIN_SETTLE,
    payload,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertSettledCaptain(contents);
}

/** The caller's own payout history, newest first. */
async function getSettlements(): Promise<Settlement[]> {
  const response = await postRequest(ServiceEndpoints.SETTLEMENTS_LIST, {}, BEARER);
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSettlementList(contents);
}

/** "Please pay me". No amount is sent — the server computes the credited
 *  unsettled balance itself. One PENDING request per person at a time.
 *  `payout` is where Nine should send the money (validated server-side too). */
async function requestSettlement(
  input: RequestSettlementInput = {}
): Promise<SettlementRequest> {
  const response = await postRequest(
    ServiceEndpoints.SETTLEMENT_REQUEST,
    buildRequestSettlementPayload(input),
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertSettlementRequest(contents);
}

/** MINE = the caller's own requests; TO_PAY = requests where the caller is the
 *  payer (a lead sees their team's; anyone else gets an empty list). Status
 *  defaults to PENDING server-side; passed explicitly so the intent is visible
 *  at the call site. */
async function getSettlementRequests(
  scope: "MINE" | "TO_PAY",
  status: SettlementRequestStatus = "PENDING"
): Promise<SettlementRequest[]> {
  const response = await postRequest(
    ServiceEndpoints.SETTLEMENT_REQUESTS,
    { scope, status },
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSettlementRequestList(contents);
}

/** Payer side: records a payout to `payeeUserId` and closes their PENDING
 *  request. Only the payee's payer may call it (a lead for their own team). */
async function recordSettlement(
  payload: RecordSettlementPayload
): Promise<Settlement> {
  const response = await postRequest(
    ServiceEndpoints.SETTLEMENT_RECORD,
    payload,
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertSettlement(contents);
}

async function cancelSettlementRequest(
  requestId: string
): Promise<SettlementRequest> {
  const response = await postRequest(
    ServiceEndpoints.SETTLEMENT_REQUEST_CANCEL,
    { requestId },
    BEARER
  );
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertSettlementRequest(contents);
}

const OnboardingApiService = {
  getCaptainSummary,
  getPendingBusinesses,
  createPendingBusiness,
  retryPaymentLink,
  createCaptain,
  updateCaptain,
  getCaptainsByLead,
  settleCaptain,
  getSettlements,
  requestSettlement,
  getSettlementRequests,
  cancelSettlementRequest,
  recordSettlement,
};

export default OnboardingApiService;
